import React, { useMemo } from 'react'
import { Typography } from '@mui/material'

import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface WeekDaysBoldProps {
  weekDays: string
}

/**
 * Renders bold days with correct punctuation
 * - Splits the input string into individual days using localized 'and' and commas
 * - Interleaves bold Typography components for days with plain commas and 'and' between them
 */
const WeekDaysBold: React.FC<WeekDaysBoldProps> = ({ weekDays }) => {
  const { t } = useCustomTranslation()

  const weekDaysToRender = useMemo(() => {
    // Split the weekDays string into an array of days
    // If the string contains the localized 'and', split by it, then split the first part by commas
    // Example: "Monday, Tuesday and Wednesday" -> ["Monday", "Tuesday", "Wednesday"]
    let days: string[] = []
    if (weekDays.includes(` ${t('common.and')} `)) {
      const [beforeAnd, afterAnd] = weekDays.split(` ${t('common.and')} `)
      days = beforeAnd.split(', ').concat(afterAnd)
    } else {
      days = weekDays.split(', ')
    }

    // Interleave bold days with plain commas and 'and'
    // Only the day names are bold, punctuation is plain text
    const result: React.ReactNode[] = []
    days.forEach((day, idx) => {
      // Render each day as bold
      result.push(
        <Typography variant="body2" component="strong" key={day + idx} sx={{ fontWeight: 'bold', display: 'inline' }}>
          {day}
        </Typography>
      )
      // Add comma after all but the last two days
      if (idx < days.length - 2) {
        result.push(', ')
      }
      // Add localized 'and' before the last day
      else if (idx === days.length - 2) {
        result.push(` ${t('common.and')} `)
      }
    })
    return result
  }, [weekDays, t])

  // Return the formatted list of days and punctuation
  return <>{weekDaysToRender}</>
}

export default WeekDaysBold
